import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight, CloudFog } from "lucide-react";
import { IMAGES } from "@/data/images";
import { HERO_BENEFITS, PHONE, PHONE_TEL } from "@/data/content";
import { btnPrimary, btnSecondary } from "@/lib/ui";

const H1_LINES = ["Zie hitte", "voordat het", "vuur wordt."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={IMAGES.hero}
          alt="Modern recyclingterrein in de avond met industriële installaties"
          className="h-[115%] w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#090D10] via-[#090D10]/80 to-[#090D10]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090D10] via-transparent to-[#090D10]/60" />
        <div className="absolute right-[18%] top-[38%] hidden md:block">
          <span className="hotspot-ring absolute -left-10 -top-10 h-20 w-20 rounded-full border-2 border-[#FF8A00]/70" />
          <span className="hotspot-ring absolute -left-6 -top-6 h-12 w-12 rounded-full border border-[#FF8A00]" style={{ animationDelay: "0.6s" }} />
          <span className="hotspot-core absolute -left-2 -top-2 h-4 w-4 rounded-full bg-[#FF8A00] shadow-[0_0_28px_6px_rgba(255,138,0,0.5)]" />
          <span className="absolute -top-20 left-6 whitespace-nowrap rounded-md border border-[#FF8A00]/50 bg-[#090D10]/80 px-3 py-1.5 font-heading text-xs font-bold text-[#FF8A00] backdrop-blur-sm">
            HOTSPOT · 78,4 °C
          </span>
        </div>
        <div className="scan-line" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-4 pb-36 pt-32 sm:px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-6 font-heading text-xs font-semibold uppercase tracking-[0.35em] text-[#62C3F0]"
        >
          Thermische branddetectie voor recyclingbedrijven
        </motion.p>

        <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-[#F5F7F9] sm:text-5xl lg:text-6xl xl:text-7xl">
          {H1_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? (
                  <>
                    <span className="bg-gradient-to-r from-[#FF8A00] to-[#E5483F] bg-clip-text text-transparent">vuur</span>{" "}
                    wordt.
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-[#CBD3D9] md:text-lg"
        >
          Hikvision thermische camera's bewaken afvalstromen, opslagvakken, bunkers, transportbanden
          en andere risicogebieden op afwijkende temperatuurstijgingen. Wanneer een ingestelde
          grenswaarde wordt overschreden, kan het systeem automatisch een voorwaarschuwing of alarm
          activeren.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-4 max-w-2xl text-sm leading-relaxed text-[#CBD3D9]/80"
        >
          Zo krijgt uw organisatie eerder inzicht in mogelijke hotspots en meer tijd om de situatie
          te controleren en volgens het afgesproken veiligheidsprotocol te handelen.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.12 }}
          className="mt-6 inline-flex max-w-2xl items-center gap-3 self-start rounded-full border border-[#FF8A00]/40 bg-[#FF8A00]/10 px-5 py-2.5 backdrop-blur-sm"
          data-testid="hero-smoke-chip"
        >
          <CloudFog size={16} className="shrink-0 text-[#FF8A00]" />
          <span className="text-xs font-semibold text-[#F5F7F9] sm:text-sm">
            Detecteert ook rook — vaak nog vóórdat hitte of vuur ontstaat
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/offerte" className={btnPrimary} data-testid="hero-cta-primary">
            Vraag een gratis locatieanalyse aan <ArrowRight size={16} />
          </Link>
          <Link to="/hoe-het-werkt" className={btnSecondary} data-testid="hero-cta-secondary">
            Bekijk hoe het werkt
          </Link>
        </motion.div>
        <motion.a
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          href={PHONE_TEL}
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#62C3F0] transition-colors hover:text-[#F5F7F9]"
          data-testid="hero-phone-link"
        >
          <Phone size={15} /> Direct advies nodig? Bel {PHONE}
        </motion.a>
      </motion.div>

      <div className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10 bg-[#090D10]/70 backdrop-blur-xl">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px lg:grid-cols-4">
          {HERO_BENEFITS.map((benefit, i) => (
            <motion.div
              key={benefit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.4 + i * 0.1 }}
              className="flex items-center gap-3 px-5 py-5 sm:px-6"
            >
              <span className="font-heading text-xs font-bold text-[#FF8A00]">0{i + 1}</span>
              <span className="text-xs font-semibold text-[#CBD3D9] sm:text-sm">{benefit}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
